import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import jwtDecode from 'jwt-decode';
import { AuthContext } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';
import api from '../services/axios';
import alertService from '../services/alerts';
import defaultPhoto from '../assets/fotoPerfil.jpg';
import '../styles/UsuariosPage.css';

function UsuariosPage() {
  const [usuarios, setUsuarios] = useState([]);
  const [filteredUsuarios, setFilteredUsuarios] = useState([]);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalUser, setModalUser] = useState(null);
  const { user } = useContext(AuthContext);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const navigate = useNavigate();
  const itemsPerPage = 5;
  const location = useLocation(); 
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1025);
  
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1025;
      setIsMobile(mobile);
      if (!mobile) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    if (window.innerWidth >= 1025) {
      setSidebarOpen(true);
    }
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [location, isMobile]);
  
  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  const handleCloseSidebar = () => {
    if (isMobile && sidebarOpen) {
      setSidebarOpen(false);
    }
  };

  const fetchUsuarios = async () => {
    try {
      const response = await api.get('/usuarios');
      const usuariosData = response.data;

      const usuariosConFoto = await Promise.all(
        usuariosData.map(async (u) => {
          const photoURL = await getPhotoUrl(u.id_usuario);
          return { ...u, photoURL };
        })
      );

      setUsuarios(usuariosConFoto);
      setFilteredUsuarios(usuariosConFoto);
      setError('');
    } catch (err) {
      if (err.response && err.response.status === 403) {
        setError('No tienes permisos para ver los usuarios');
      } else {
        setError('Error al obtener la lista de usuarios');
      }
      console.error(err);
    }
  };

  useEffect(() => {
    fetchUsuarios();
  }, []);

  const handleSearch = (event) => {
    const query = event.target.value.toLowerCase();
    setSearchQuery(query);
    setCurrentPage(1);
    const filtered = usuarios.filter((usuario) => {
      const nombre = usuario.nombre?.toLowerCase() || '';
      const paterno = usuario.apellido_paterno?.toLowerCase() || '';
      const materno = usuario.apellido_materno?.toLowerCase() || '';
      return (
        nombre.includes(query) ||
        paterno.includes(query) ||
        materno.includes(query)
      );
    });
    setFilteredUsuarios(filtered);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const getPhotoUrl = async (id) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/usuarios/${id}/foto`);
      if (!response.ok) return defaultPhoto;

      const blob = await response.blob();
      if (blob.size === 0) return defaultPhoto;

      const contentType = response.headers.get('Content-Type');
      if (!contentType || !contentType.includes('image')) return defaultPhoto;

      return URL.createObjectURL(blob);
    } catch (error) {
      return defaultPhoto;
    }
  };

  const handleAdd = () => {
    navigate('/cuentas/agregar');
  };

  const handleEdit = (id_usuario) => {
    if (user && user.id_usuario === id_usuario) {
      alert('No puedes editar tu propia cuenta mientras estás logueado con ella.');
      return;
    }
    navigate(`/cuentas/editar/${id_usuario}`);
  };

  const handleDelete = async (id_usuario) => {
    if (user && user.id_usuario === id_usuario) {
      await alertService.warning(
        'Acción No Permitida',
        'No puedes eliminar tu propia cuenta mientras estás logueado con ella.'
      );
      return;
    }

    const confirmed = await alertService.confirm(
      'Eliminar Usuario',
      '¿Estás seguro de que deseas eliminar este usuario? Esta acción no se puede deshacer.',
      {
        confirmButtonText: 'Eliminar',
        cancelButtonText: 'Cancelar'
      }
    );

    if (!confirmed.isConfirmed) return;

    try {
      await api.delete(`/usuarios/${id_usuario}`);
      setUsuarios((prev) => prev.filter((u) => u.id_usuario !== id_usuario));
      setFilteredUsuarios((prev) => prev.filter((u) => u.id_usuario !== id_usuario));
      await alertService.success(
        'Usuario Eliminado',
        'El usuario ha sido eliminado exitosamente.'
      );
    } catch (err) {
      let errorMessage = 'Error al eliminar el usuario';
      
      if (err.response && err.response.status === 409) {
        errorMessage = err.response.data.error || 'No se puede eliminar el usuario porque está siendo utilizado en otra tabla.';
      } else if (err.response && err.response.status === 403) {
        errorMessage = 'No tienes permisos para eliminar usuarios.';
      } else if (err.response && err.response.status === 400) {
        errorMessage = err.response.data.error || 'No puedes eliminar tu propia cuenta.';
      }
      
      await alertService.error('Error', errorMessage);
    }
  };

  const handlePhotoClick = (usuario) => {
    setModalUser(usuario);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalUser(null);
  };

  const usuariosToShow = searchQuery.trim() === ''
    ? filteredUsuarios.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
    : filteredUsuarios;

  const nextPage = () => {
    if (currentPage * itemsPerPage < filteredUsuarios.length) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  
  return (
    <div className="dashboard-container">
      {(!isMobile || (isMobile && sidebarOpen)) && (
        <Sidebar
          onLogoClick={() => {
            if (isMobile) setSidebarOpen(false);
          }}
          onLinkClick={handleCloseSidebar}
        />
      )}
      
      <div className="main-content" onClick={handleCloseSidebar}>
        <div className="dashboard-title-card">
          <button className="menu-toggle" onClick={toggleSidebar}>
            <i className="fas fa-bars"></i>
          </button>
          <div className="dashboard-title-section">
            <h1 className="dashboard-title">Usuarios</h1>
            <span className="dashboard-title-sub">Gestiona todos los usuarios del sistema</span>
          </div>
        </div>

        {error && (
          <div className="error-message">
            <p>{error}</p>
          </div>
        )}

        <div className="search-filter-container">
          <input
            type="text"
            placeholder="Buscar por nombre o apellido..."
            value={searchQuery}
            onChange={handleSearch}
            className="search-input"
          />
        </div>

        <div className="add-button-container">
          <button className="add-button" onClick={handleAdd}>
            <i className="fas fa-plus"></i>
            Agregar Usuario
          </button>
        </div>

        <div className="futuristic-table-wrapper">
          <div className="table-header-futuristic">
            <div className="header-stats">
              <div className="stat-item">
                <span className="stat-label">Total Usuarios</span>
                <span className="stat-value">{usuarios.length}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Mostrando</span>
                <span className="stat-value">{usuariosToShow.length}</span>
              </div>
            </div>
          </div>
          
          <div className="futuristic-table">
            {usuariosToShow && usuariosToShow.length > 0 ? (
              usuariosToShow.map((usuario) => (
                <div key={usuario.id_usuario} className="table-row-futuristic">
                  <div className="row-header">
                    <div className="usuario-id">
                      <span className="id-label">ID</span>
                      <span className="id-value">{usuario.id_usuario}</span>
                    </div>
                    <div className="usuario-photo">
                      <img
                        src={usuario.photoURL || defaultPhoto}
                        alt="Usuario"
                        className="foto-usuario"
                        onError={(e) => (e.target.src = defaultPhoto)}
                        onClick={() => handlePhotoClick(usuario)}
                      />
                    </div>
                    <div className="usuario-name">
                      <span className="name-text">
                        {usuario.nombre} {usuario.apellido_paterno} {usuario.apellido_materno}
                      </span>
                    </div>
                    <div className="action-buttons">
                      <button
                        className="action-btn edit-btn"
                        onClick={() => handleEdit(usuario.id_usuario)}
                        title="Editar usuario"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        className="action-btn delete-btn"
                        onClick={() => handleDelete(usuario.id_usuario)}
                        title="Eliminar usuario"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                  
                  <div className="row-content">
                    <div className="info-grid">
                      <div className="info-item">
                        <span className="info-label">Nombre Completo</span>
                        <span className="info-value">
                          {usuario.nombre} {usuario.apellido_paterno} {usuario.apellido_materno}
                        </span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Correo</span>
                        <span className="info-value">{usuario.correo}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Rol</span>
                        <span className="role-badge">{usuario.rol}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Fecha de Creación</span>
                        <span className="info-value">{formatDate(usuario.fecha_creacion)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state">
                <div className="empty-icon">
                  <i className="fas fa-users"></i>
                </div>
                <h3>No hay usuarios para mostrar</h3>
                <p>No se encontraron usuarios que coincidan con tu búsqueda.</p>
              </div>
            )}
          </div>
        </div>

        {searchQuery.trim() === '' && (
          <div className="pagination-container">
            {currentPage > 1 && (
              <button onClick={prevPage}>
                <i className="fas fa-chevron-left"></i>
                Anterior
              </button>
            )}
            <span>
              Página {currentPage} de {Math.ceil(filteredUsuarios.length / itemsPerPage)}
            </span>
            {currentPage * itemsPerPage < filteredUsuarios.length && (
              <button onClick={nextPage}>
                Siguiente
                <i className="fas fa-chevron-right"></i>
              </button>
            )}
          </div>
        )}

        {modalOpen && modalUser && (
          <div className="usuarios-modal-overlay" onClick={closeModal}>
            <div className="usuarios-modal" onClick={(e) => e.stopPropagation()}>
              <h2>Foto de {modalUser.nombre}</h2>
              <img
                src={modalUser.photoURL || defaultPhoto}
                alt="Imagen ampliada"
                className="usuarios-modal-photo"
              />
              <p>
                Fecha de creación: <strong>{formatDate(modalUser.fecha_creacion)}</strong>
              </p>
              <button onClick={closeModal} className="usuarios-close-modal-button">
                Cerrar
              </button>
            </div>
          </div>
        )}
      </div>
      
      {isMobile && sidebarOpen && (
        <div className="overlay" onClick={handleCloseSidebar}></div>
      )}
    </div>
  );
}

export default UsuariosPage;
